import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import { FETCH_VEHICLES } from '../../../../gql/queries/vehicles';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';

// Constants
import { formTeamManagementNaming } from '../../../../components/forms/AddTeamForm/constants';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

// Types
import { Team } from './types';

type ReferenceData = {
    concessions: { id: string; name: string }[];
    roads: { id: string; name: string }[];
    users: { id: string; name: string; surName: string }[];
    vehicles: { id: string; plate: string }[];
};

const loadTeamsData = async (
    userData: AuthContextType | null,
    refData: ReferenceData
): Promise<Team[]> => {
    try {
        if (!userData?.user) return [];

        const {
            data: { teams },
        } = await client.query({
            query: FETCH_TEAMS,
            fetchPolicy: 'network-only',
        });

        // Map teams with resolved names
        const teamsWithNames: Team[] = teams.map((team: Team) => ({
            ...team,
            concessionNames: team.concessionIDs
                ?.map((id) => refData.concessions.find((c) => c.id === id)?.name)
                .filter(Boolean),
            roadNames: team.roadIDs
                ?.map((id) => refData.roads.find((r) => r.id === id)?.name)
                .filter(Boolean),
            userNames: team.userIDs
                ?.map((id) => {
                    const user = refData.users.find((u) => u.id === id);
                    return user ? `${user.name} ${user.surName}` : null;
                })
                .filter(Boolean),
            vehiclePlates: team.vehicleIDs
                ?.map((id) => refData.vehicles.find((v) => v.id === id)?.plate)
                .filter(Boolean),
        }));

        return teamsWithNames;
    } catch (err) {
        Sentry.captureException(err);
        console.log('> Team management loading error:', err);
        return [];
    }
};

export const TeamsManagementLoader: (
    userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    try {
        // Fetch reference data - GET_SELECTORS_DATA contains concessions and roads
        const [selectorsResult, usersResult, vehiclesResult] = await Promise.all([
            client.query({ query: GET_SELECTORS_DATA }),
            client.query({ query: FETCH_USERS }),
            client.query({ query: FETCH_VEHICLES }),
        ]);

        const { concessions, roads } = selectorsResult.data;
        const { users } = usersResult.data;
        const { vehicles } = vehiclesResult.data;

        // Load teams with resolved names
        const teams = await loadTeamsData(userData, {
            concessions,
            roads,
            users,
            vehicles,
        });

        // Get cached form data
        let cacheData =
            sessionStorage.getItem(formTeamManagementNaming.NEW_TEAM_MANAGEMENT_FORM) || {};

        if (Object.keys(cacheData).length > 0) {
            cacheData = JSON.parse(decompressSessionStorage(cacheData as string));
        }

        return {
                teams,
                cacheData: {
                    ...cacheData,
                    concessions,
                    roads,
                    users,
                    vehicles: vehicles.map((v: { id: string; plate: string }) => ({
                        id: v.id,
                        plate: v.plate,
                    })),
                },
            };
    } catch (err) {
        Sentry.captureException(err);
        console.log('> Teams loader error:', err);
        return { teams: [], cacheData: {} };
    }
};

  return { data: load() };
};
